import BigNumber from 'bignumber.js';
import { useTranslation } from 'src/hooks';
import useSystemCurrency from 'src/hooks/useSystemCurrency';
import { requiredValidator } from 'src/utils/validators/requiredValidator';
import { CsvData } from '../types/CsvData';
import { TableColumnKey } from '../types/Table';

const useValidation = () => {
  const originalCheckRequired = requiredValidator('');
  const localCheckRequired = (value: string | undefined) => originalCheckRequired('', value);

  const { SystemCurrencyEnum } = useSystemCurrency({ tokenNameFilter: 'HKDM' });
  const systemCurrencyTuple = Object.entries(SystemCurrencyEnum);

  const { t, translate } = useTranslation('clientCreditAllocation');
  const getIsRequiredMsg = (key: string) =>
    translate('validation.isRequired', {
      fieldName: t(key),
    });

  const validateItem = (item: CsvData) => {
    let errorMessage = '';

    //check customer no
    let isPfhNoValid = localCheckRequired(item.pfh_no).isValid;
    if (!isPfhNoValid) {
      errorMessage += `${getIsRequiredMsg(TableColumnKey.pfhNo)}, `;
    }

    //check credit currency
    let isCreditCurrencyValid = localCheckRequired(item.credit_currency).isValid;
    if (!isCreditCurrencyValid) {
      errorMessage += `${getIsRequiredMsg(TableColumnKey.CreditCurrency)}, `;
    } else if (!systemCurrencyTuple.some(([key, _]) => key === item.credit_currency)) {
      isCreditCurrencyValid = false;
      errorMessage += `${translate('validation.isInValid', {
        fieldName: t(TableColumnKey.CreditCurrency),
      })}, `;
    }

    //check credit amount
    let isCreditAmountValid = localCheckRequired(item.credit_amount).isValid;
    if (!isCreditAmountValid) {
      errorMessage += `${getIsRequiredMsg(TableColumnKey.CreditAmount)}, `;
    }
    const creditAmountNumber = new BigNumber(item.credit_amount ?? '');
    //check is not number
    if (isCreditAmountValid && creditAmountNumber.isNaN()) {
      isCreditAmountValid = false;
      errorMessage += `${t('isNotNumber', {
        fieldName: t(TableColumnKey.CreditAmount),
      })}, `;
    }
    //check is negative number
    if (isCreditAmountValid && creditAmountNumber.isLessThanOrEqualTo(0)) {
      isCreditAmountValid = false;
      errorMessage += `${t('isSmallerThanZero', {
        fieldName: t(TableColumnKey.CreditAmount),
      })}, `;
    }
    //check decimal place
    if (isCreditAmountValid) {
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(creditAmountNumber.toString())) {
        isCreditAmountValid = false;
        errorMessage += `${t('twoDecimalPlacesError', {
          fieldName: t(TableColumnKey.CreditAmount),
        })}, `;
      }
    }

    const allCheckings = [isPfhNoValid, isCreditCurrencyValid, isCreditAmountValid];

    const isValidationPassed = allCheckings.every((item) => item === true);

    return {
      isValidationPassed,
      errorMessage,
    };
  };

  return { validateItem };
};
export default useValidation;
